import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useDialogState, useSearchResults, useSuggestions } from './hooks';
import { AritoDialog, AritoIcon } from '@/components/custom/arito';
import { normalizeEndpoint } from '@/lib/normalize-endpoint';
import { SearchFieldInput } from '../search-field-input';
import { useAuth } from '@/contexts/auth-context';
import { ActionsButton } from './ActionsButton';
import { DataTable } from './DataTable';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

export interface ResolveResult<T = any> {
  items: T[];
  notFoundCodes: string[];
}

export interface SearchFieldRef<T = any> {
  /**
   * Resolves the current input value to full objects with UUIDs.
   * Call this before form submit to ensure all typed codes are resolved.
   * @returns Promise<ResolveResult<T>> - Resolved items with UUIDs and not found codes
   */
  resolveCurrentInput: () => Promise<ResolveResult<T>>;
  /**
   * Returns the currently selected items
   */
  getSelectedItems: () => T[];
  /**
   * Returns the current input value
   */
  getInputValue: () => string;
  /**
   * Returns true if currently resolving/searching
   */
  isResolving: () => boolean;
  /**
   * Returns the current error message if any
   */
  getError: () => string | null;
  /**
   * Clears the current error
   */
  clearError: () => void;
}

interface SearchFieldPropsBase<T = any> {
  name?: string;
  type?: string;
  disabled?: boolean;
  className?: string;
  columnDisplay?: keyof T;
  rows?: T[];
  searchEndpoint?: string;
  otherSearchEndpoint?: string;
  searchColumns?: any[];
  displayRelatedField?: keyof T;
  classNameRelatedField?: string;
  relatedFieldValue?: string;
  dialogTitle?: string;
  hideRowNumberColumn?: boolean;
  headerFields?: React.ReactNode;
  placeholder?: string;
  value?: any;
  onValueChange?: (value: any) => void;
  onBeforeOpen?: () => void | Promise<void>;
}

interface SearchFieldPropsSingle<T = any> extends SearchFieldPropsBase<T> {
  multiple?: false;
  onRowSelection?: (row: T) => void;
  separator?: never;
}

interface SearchFieldPropsMultiple<T = any> extends SearchFieldPropsBase<T> {
  multiple: true;
  onRowSelection?: (rows: T[]) => void;
  separator?: string;
}

type SearchFieldProps<T = any> = SearchFieldPropsSingle<T> | SearchFieldPropsMultiple<T>;

const SearchFieldInner = <T = any,>(
  {
    type = 'text',
    value = '',
    searchEndpoint,
    searchColumns = [],
    className,
    dialogTitle = 'Danh mục',
    placeholder,
    onRowSelection,
    onValueChange,
    displayRelatedField,
    classNameRelatedField,
    relatedFieldValue,
    disabled,
    columnDisplay = '' as keyof T,
    rows,
    otherSearchEndpoint,
    onBeforeOpen,
    multiple = false,
    separator = ','
  }: SearchFieldProps<T>,
  ref: React.ForwardedRef<SearchFieldRef<T>>
) => {
  const { entity } = useAuth();

  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [multiInputValue, setMultiInputValue] = useState('');
  const [isMultiSearching, setIsMultiSearching] = useState(false);
  const [multiSearchError, setMultiSearchError] = useState<string | null>(null);
  const [isInputDirty, setIsInputDirty] = useState(false);
  const hasCheckboxSelectionColumn = searchColumns.some(
    (column: any) => column?.field === '' && column?.type === 'boolean'
  );

  useEffect(() => {
    if (multiple && Array.isArray(value)) {
      setSelectedItems(value);
      if (!isInputDirty) {
        setMultiInputValue(
          value
            .map((item: any) => {
              if (typeof item === 'string') return item;
              return item[columnDisplay as string] || '';
            })
            .join(', ')
        );
      }

      if (value.length === 0) {
        setMultiSearchError(null);
      }
    }
  }, [multiple, value, columnDisplay, isInputDirty]);

  const fetchByMultipleCodes = useCallback(
    async (input: string): Promise<ResolveResult<T>> => {
      if (!input.trim() || !entity?.slug || !searchEndpoint) {
        return { items: [], notFoundCodes: [] };
      }

      const codes = input
        .split(separator)
        .map(code => code.trim())
        .filter(code => code.length > 0);

      if (codes.length === 0) return { items: [], notFoundCodes: [] };

      setIsMultiSearching(true);
      setMultiSearchError(null);

      try {
        const results: T[] = [];
        const notFoundCodes: string[] = [];

        for (const code of codes) {
          try {
            const url = `entities/${entity.slug}/erp/${normalizeEndpoint(searchEndpoint)}`;
            const response = await api.get(url, { params: { search: code } });

            const items: T[] = response.data?.results || response.data?.data || response.data || [];

            const exactMatch = items.find(
              (item: any) =>
                item[columnDisplay as string]?.toString().trim().toLowerCase() === code.trim().toLowerCase()
            );

            if (exactMatch) {
              const isDuplicate = results.some((r: any) => r.uuid === (exactMatch as any).uuid);
              if (!isDuplicate) {
                results.push(exactMatch);
              }
            } else {
              notFoundCodes.push(code);
            }
          } catch {
            notFoundCodes.push(code);
          }
        }

        if (notFoundCodes.length > 0) {
          setMultiSearchError(`Không tìm thấy: ${notFoundCodes.join(', ')}`);
        }

        return { items: results, notFoundCodes };
      } catch {
        setMultiSearchError('Có lỗi xảy ra khi tìm kiếm');
        return { items: [], notFoundCodes: codes };
      } finally {
        setIsMultiSearching(false);
      }
    },
    [entity?.slug, searchEndpoint, separator, columnDisplay]
  );

  const handleMultiSearch = useCallback(async () => {
    if (!multiInputValue.trim()) {
      setSelectedItems([]);
      setMultiSearchError(null);
      setIsInputDirty(false);
      (onRowSelection as (rows: T[]) => void)?.([]);
      return;
    }

    const { items, notFoundCodes } = await fetchByMultipleCodes(multiInputValue);

    if (notFoundCodes.length === 0) {
      setSelectedItems(items);
      (onRowSelection as (rows: T[]) => void)?.(items);
      if (items.length > 0) {
        setMultiInputValue(items.map((a: any) => a[columnDisplay as string]).join(', '));
      }
      setIsInputDirty(false);
    }
  }, [multiInputValue, fetchByMultipleCodes, onRowSelection, columnDisplay]);

  const resolveCurrentInput = useCallback(async (): Promise<ResolveResult<T>> => {
    if (!multiple) {
      return { items: selectedItems, notFoundCodes: [] };
    }

    if (!multiInputValue.trim()) {
      setSelectedItems([]);
      setMultiSearchError(null);
      setIsInputDirty(false);
      (onRowSelection as (rows: T[]) => void)?.([]);
      return { items: [], notFoundCodes: [] };
    }

    const currentCodes = selectedItems.map((item: any) => item[columnDisplay as string]?.toLowerCase());
    const inputCodes = multiInputValue
      .split(separator)
      .map(code => code.trim().toLowerCase())
      .filter(code => code.length > 0);

    const allCodesResolved = inputCodes.every(code => currentCodes.includes(code));
    if (allCodesResolved && inputCodes.length === currentCodes.length) {
      setMultiSearchError(null);
      return { items: selectedItems, notFoundCodes: [] };
    }

    const result = await fetchByMultipleCodes(multiInputValue);

    if (result.items.length > 0 && result.notFoundCodes.length === 0) {
      setSelectedItems(result.items);
      setMultiInputValue(result.items.map((a: any) => a[columnDisplay as string]).join(', '));
      (onRowSelection as (rows: T[]) => void)?.(result.items);
      setIsInputDirty(false);
    }

    return result;
  }, [multiple, multiInputValue, selectedItems, columnDisplay, separator, fetchByMultipleCodes, onRowSelection]);

  useImperativeHandle(
    ref,
    () => ({
      resolveCurrentInput,
      getSelectedItems: () => selectedItems,
      getInputValue: () => multiInputValue,
      isResolving: () => isMultiSearching,
      getError: () => multiSearchError,
      clearError: () => setMultiSearchError(null)
    }),
    [resolveCurrentInput, selectedItems, multiInputValue, isMultiSearching, multiSearchError]
  );

  const handleRemoveItem = useCallback(
    (uuid: string) => {
      const newItems = selectedItems.filter((item: any) => {
        const itemIdentifier = typeof item === 'string' ? item : item.uuid;
        return itemIdentifier !== uuid;
      });
      setSelectedItems(newItems);
      setMultiInputValue(
        newItems
          .map((a: any) => {
            if (typeof a === 'string') return a;
            return a[columnDisplay as string];
          })
          .join(', ')
      );
      setIsInputDirty(false);
      (onRowSelection as (rows: T[]) => void)?.(newItems);
    },
    [selectedItems, columnDisplay, onRowSelection]
  );

  const handleClearAll = useCallback(() => {
    setSelectedItems([]);
    setMultiInputValue('');
    setMultiSearchError(null);
    setIsInputDirty(false);
    (onRowSelection as (rows: T[]) => void)?.([]);
  }, [onRowSelection]);

  const handleMultiKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleMultiSearch();
      }
    },
    [handleMultiSearch]
  );

  const handleRowSelectionSingle = (row: T | null) => {
    if (onRowSelection && !multiple) {
      (onRowSelection as (row: T | null) => void)(row);
    }
  };

  const { searchDialogOpen, setSearchDialogOpen, isFullScreen, handleSearchClick, toggleFullScreen } = useDialogState();
  const { searchResults, setSearchResults, fetchSearchResults, isLoading, totalItems, currentPage, handlePageChange } =
    useSearchResults<T>(searchEndpoint, otherSearchEndpoint, { pageSize: 10, autoFetch: false });

  const {
    selectedObj,
    setSelectedObj,
    localSelectedObj,
    setLocalSelectedObj,
    inputValue,
    highlightedIndex,
    setHighlightedIndex,
    suggestionContainerRef,
    suggestionDropdownRef,
    suggestions,
    shouldShowDropdown,
    showNoResults,
    primaryField,
    suggestionColumns,
    clearSelection,
    handleSuggestionSelect,
    handleInputKeyDown,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    getColumnDisplayValue,
    getColumnStyle,
    resolveFieldValue,
    goToPreviousPage,
    goToNextPage,
    canGoPrevious,
    canGoNext,
    showPagination,
    dropdownPositionStyle
  } = useSuggestions<T>({
    value: multiple ? '' : value,
    searchEndpoint,
    otherSearchEndpoint,
    rows,
    columnDisplay,
    searchColumns,
    displayRelatedField,
    onRowSelection: handleRowSelectionSingle,
    onValueChange,
    fetchSearchResults,
    searchResults,
    setSearchResults,
    isLoading,
    handlePageChange,
    currentPage,
    totalItems,
    pageSize: 10
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const isInitialRender = useRef(true);
  const previousValueRef = useRef(inputValue);
  const shouldRefocusRef = useRef(false);

  useEffect(() => {
    if (multiple) return;

    if (isInitialRender.current) {
      isInitialRender.current = false;
      previousValueRef.current = inputValue;
      return;
    }

    if (inputValue !== previousValueRef.current) {
      previousValueRef.current = inputValue;
      if (shouldRefocusRef.current && inputRef.current) {
        shouldRefocusRef.current = false;
        inputRef.current.focus({ preventScroll: true });
      }
    }
  }, [inputValue, multiple]);

  if (disabled) {
    const textValue = multiple ? multiInputValue : inputValue;
    return (
      <div
        className={cn(
          'flex h-[32px] w-full min-w-40 items-center overflow-hidden text-ellipsis whitespace-nowrap px-[8px] text-[13px] text-[#00000099]',
          className
        )}
        title={textValue?.toString() || ''}
      >
        {textValue}
      </div>
    );
  }

  if (multiple) {
    return (
      <div className={cn('relative flex flex-col', className)}>
        <div className='flex items-center'>
          <SearchFieldInput
            type={type}
            disabled={disabled || isMultiSearching}
            className={cn('w-40', className)}
            value={multiInputValue}
            autoComplete='off'
            onKeyDown={handleMultiKeyDown}
            onChange={e => {
              setMultiInputValue(e.target.value);
              setIsInputDirty(true);
              onValueChange?.(e.target.value);
            }}
            onBlur={handleMultiSearch}
            placeholder={placeholder}
            onSearchClick={
              isMultiSearching
                ? undefined
                : async event => {
                    event.preventDefault();
                    event.stopPropagation();
                    try {
                      await onBeforeOpen?.();
                    } catch {
                      // Ignore pre-open errors
                    }
                    handleSearchClick();
                  }
            }
          />
        </div>

        {/* Dialog for multiple mode */}
        <AritoDialog
          open={searchDialogOpen}
          onClose={() => setSearchDialogOpen(false)}
          disableBackdropClose={false}
          disableEscapeKeyDown={false}
          fullScreen={isFullScreen}
          onFullscreenToggle={toggleFullScreen}
          title={dialogTitle}
          titleIcon={<AritoIcon icon={584} className='mx-2' />}
          actions={
            <ActionsButton
              selectedSearchResult={hasCheckboxSelectionColumn ? true : localSelectedObj}
              onConfirm={() => {
                if (hasCheckboxSelectionColumn) {
                  setLocalSelectedObj(null);
                  setSearchDialogOpen(false);
                  return;
                }

                if (localSelectedObj) {
                  const isDuplicate = selectedItems.some((item: any) => item.uuid === (localSelectedObj as any).uuid);
                  if (!isDuplicate) {
                    const newItems = [...selectedItems, localSelectedObj];
                    setSelectedItems(newItems);
                    setMultiInputValue(newItems.map((a: any) => a[columnDisplay as string]).join(', '));
                    (onRowSelection as (rows: T[]) => void)?.(newItems);
                  }
                  setLocalSelectedObj(null);
                  setSearchDialogOpen(false);
                } else {
                  setLocalSelectedObj(null);
                  setSearchDialogOpen(false);
                }
              }}
              onCancel={() => {
                setLocalSelectedObj(null);
                setSearchDialogOpen(false);
              }}
            />
          }
          classNameContent='max-h-[400px] w-[850px]'
        >
          <DataTable
            searchEndpoint={searchEndpoint}
            otherSearchEndpoint={otherSearchEndpoint}
            searchColumns={searchColumns}
            columnDisplay={columnDisplay}
            rows={rows}
            onRowSelection={setLocalSelectedObj}
            onSelectedObjChange={obj => obj && setLocalSelectedObj(obj)}
          />
        </AritoDialog>
      </div>
    );
  }

  return (
    <div ref={suggestionContainerRef} className={cn('relative flex items-center', className)}>
      <SearchFieldInput
        type={type}
        disabled={disabled}
        className={cn('w-40', className)}
        value={inputValue}
        inputRef={inputRef}
        autoComplete='off'
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          const key = e.key.toLowerCase();
          if ((e.ctrlKey || e.metaKey) && key === 'a') {
            e.stopPropagation();
            return;
          }
          if (e.key === ' ') e.stopPropagation();
          handleInputKeyDown(e);
        }}
        onChange={event => {
          shouldRefocusRef.current = true;
          handleInputChange(event.target.value);
        }}
        placeholder={placeholder}
        onSearchClick={async event => {
          event.preventDefault();
          event.stopPropagation();
          try {
            await onBeforeOpen?.();
          } catch {
            // Ignore pre-open errors to not block opening dialog
          }
          handleSearchClick();
        }}
        onMouseDown={event => {
          if (event.button === 2) {
            event.preventDefault();
          }
        }}
      />

      {!disabled &&
        shouldShowDropdown &&
        createPortal(
          <div
            ref={suggestionDropdownRef}
            className='fixed z-[9999] h-auto w-auto min-w-80 rounded-md border border-gray-200 bg-white shadow-lg'
            style={dropdownPositionStyle}
          >
            {isLoading && <div className='px-3 py-2 text-sm text-gray-500'>Đang tìm kiếm...</div>}

            {!isLoading &&
              suggestions.map((row, index) => {
                const isActive = index === highlightedIndex;
                const columnsToRender = suggestionColumns.length > 0 ? suggestionColumns : [{ field: primaryField }];
                const primaryText =
                  getColumnDisplayValue(row, columnsToRender[0]) || resolveFieldValue(row, primaryField);
                const itemKey = (row as any)?.uuid || (row as any)?.id || primaryText || index;

                return (
                  <button
                    type='button'
                    key={itemKey}
                    className={cn(
                      'flex h-7 w-full cursor-pointer items-stretch border-b border-gray-200 text-left text-[11px] transition-colors last:border-b-0',
                      isActive ? 'bg-amber-100 text-gray-900' : 'hover:bg-gray-100'
                    )}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={event => {
                      event.preventDefault();
                      handleSuggestionSelect(row);
                    }}
                  >
                    {columnsToRender.map((column, columnIndex) => (
                      <span
                        key={`${column?.field ?? columnIndex}`}
                        className={cn(
                          'flex min-w-0 items-center truncate px-3 py-2',
                          columnIndex === 0 ? 'font-medium text-gray-900' : 'text-gray-600'
                        )}
                        style={getColumnStyle(column, columnIndex)}
                      >
                        {getColumnDisplayValue(row, column)}
                      </span>
                    ))}
                  </button>
                );
              })}

            {showPagination && (
              <div className='flex items-center justify-end gap-2 px-2 py-2'>
                <button
                  type='button'
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded border border-gray-200 text-xs text-gray-600 transition-colors',
                    canGoPrevious ? 'hover:bg-gray-100' : 'cursor-not-allowed opacity-50'
                  )}
                  disabled={!canGoPrevious || isLoading}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => goToPreviousPage()}
                >
                  &lt;
                </button>
                <button
                  type='button'
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded border border-gray-200 text-xs text-gray-600 transition-colors',
                    canGoNext ? 'hover:bg-gray-100' : 'cursor-not-allowed opacity-50'
                  )}
                  disabled={!canGoNext || isLoading}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => goToNextPage()}
                >
                  &gt;
                </button>
              </div>
            )}

            {showNoResults && <div className='px-3 py-2 text-sm text-gray-500'>Không tìm thấy kết quả phù hợp</div>}
          </div>,
          document.body
        )}

      {displayRelatedField && (
        <div
          className={cn(
            'ml-0 mt-1 flex-1 text-ellipsis whitespace-nowrap text-left text-sm text-gray-700 sm:ml-3 sm:mt-0',
            classNameRelatedField
          )}
        >
          {relatedFieldValue || (selectedObj?.[displayRelatedField] as string)}
        </div>
      )}

      <AritoDialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        disableBackdropClose={false}
        disableEscapeKeyDown={false}
        fullScreen={isFullScreen}
        onFullscreenToggle={toggleFullScreen}
        title={dialogTitle}
        titleIcon={<AritoIcon icon={584} className='mx-2' />}
        actions={
          <ActionsButton
            selectedSearchResult={localSelectedObj}
            onConfirm={() => {
              if (localSelectedObj) {
                setSelectedObj(localSelectedObj);
                handleRowSelectionSingle(localSelectedObj);
                setSearchDialogOpen(false);
              } else {
                handleRowSelectionSingle(null);
                setSearchDialogOpen(false);
              }
            }}
            onCancel={() => {
              clearSelection();
              setLocalSelectedObj(null);
              setSearchDialogOpen(false);
            }}
          />
        }
        classNameContent='max-h-[400px] w-[850px]'
      >
        <DataTable
          searchEndpoint={searchEndpoint}
          otherSearchEndpoint={otherSearchEndpoint}
          searchColumns={searchColumns}
          columnDisplay={columnDisplay}
          rows={rows}
          onRowSelection={setLocalSelectedObj}
          onSelectedObjChange={obj => obj && setLocalSelectedObj(obj)}
        />
      </AritoDialog>
    </div>
  );
};

export const SearchField = forwardRef(SearchFieldInner) as <T = any>(
  props: SearchFieldProps<T> & { ref?: React.ForwardedRef<SearchFieldRef<T>> }
) => React.ReactElement;
